import { Editor, Path, Range } from 'slate';
import type { NodeEntry } from 'slate';

import type { RenderContentBlockOptions } from './content-block';
import { renderContentBlockFns } from './content-block';
import type {
  InsertTableRowEdit,
  RemoveTableRowEdit,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  TableBorder,
} from '../../slate-api';
import {
  isInsertTableCellEdit,
  isInsertTableRowEdit,
  isRemoveTableCellEdit,
  isRemoveTableRowEdit,
  isRenderModelTableCellBoundingBox,
  isRenderModelTableRowBoundingBox,
  isTableCellEntry,
  Queries,
} from '../../slate-api';
import { arrayEntries, ptToPx } from '../../util';
import {
  activeSelectionColor,
  inactiveSelectionColor,
  insertTableCellColor,
  removeTableCellColor,
} from '../colors';
import type { RenderContext } from '../engine/render-context';
import type { BaseRenderNodeOptions } from '../engine/rendering-data';
import { CANVAS_Z_INDICES } from '../z-indices';

export function renderTable(
  renderContext: RenderContext,
  rMTableEntry: NodeEntry<RenderModelTable>,
  options: BaseRenderNodeOptions
) {
  const { cMPath, editor } = renderContext;
  const [rMTable] = rMTableEntry;
  const { children, columnWidthsPx } = rMTable;

  const rowBoundingBoxes = Queries.getRMBoundingBoxes(editor, rMTableEntry).filter(
    isRenderModelTableRowBoundingBox
  );

  const gridSelectedCellPathKeys = renderContext.withContextSelector(
    'gridSelectionInfo',
    gridSelectionInfo => {
      if (!gridSelectionInfo || !cMPath || !Path.equals(gridSelectionInfo.tableEntry[1], cMPath))
        return null;

      const cellsInRect = Queries.getTableCellEntriesInGridRect(
        gridSelectionInfo.tableEntry,
        gridSelectionInfo.rect
      );

      return new Set(
        cellsInRect.flatMap(({ tableCellEntry, verticalMergeContinuationEntries }) =>
          [tableCellEntry, ...verticalMergeContinuationEntries].map(([, path]) => path.join(','))
        )
      );
    }
  );

  for (const [rowIndex, rowBoundingBox] of arrayEntries(rowBoundingBoxes)) {
    renderContext.renderChild(
      rowBoundingBox,
      { 'table-row': renderTableRow },
      {
        ...options,
        cMTablePath: cMPath,
        columnWidthsPx,
        gridSelectedCellPathKeys,
        nextRMTableRow: children.at(rowIndex + 1) ?? null,
        prevRMTableRow: rowIndex > 0 ? (children.at(rowIndex - 1) ?? null) : null,
      } satisfies RenderTableRowOptions,
      { isLastChild: rowIndex === rowBoundingBoxes.length - 1 }
    );
  }
}

interface RenderTableRowOptions
  extends
    BaseRenderNodeOptions,
    Pick<
      RenderTableCellOptions,
      | 'cMTablePath'
      | 'columnWidthsPx'
      | 'gridSelectedCellPathKeys'
      | 'nextRMTableRow'
      | 'prevRMTableRow'
    > {}

function renderTableRow(
  renderContext: RenderContext,
  rMTableRowEntry: NodeEntry<RenderModelTableRow>,
  options: RenderTableRowOptions
) {
  const { editor } = renderContext;
  const [rMTableRow] = rMTableRowEntry;
  const { children } = rMTableRow;
  const suggestedEdits = rMTableRow.suggestedEdits ?? [];
  const insertRowEdit = suggestedEdits.find(isInsertTableRowEdit);
  const removeRowEdit = suggestedEdits.find(isRemoveTableRowEdit);
  const { columnWidthsPx, gridSelectedCellPathKeys, nextRMTableRow, prevRMTableRow } = options;

  const cellBoundingBoxes = Queries.getRMBoundingBoxes(editor, rMTableRowEntry).filter(
    isRenderModelTableCellBoundingBox
  );

  for (const [cellIndex, cellBoundingBox] of arrayEntries(cellBoundingBoxes)) {
    renderContext.renderChild(
      cellBoundingBox,
      { 'table-cell': renderTableCell },
      {
        ...options,
        columnWidthsPx,
        gridSelectedCellPathKeys,
        insertRowEdit,
        nextRMTableCell: children.at(cellIndex + 1) ?? null,
        nextRMTableRow,
        prevRMTableCell: cellIndex > 0 ? (children.at(cellIndex - 1) ?? null) : null,
        prevRMTableRow,
        removeRowEdit,
      } satisfies RenderTableCellOptions,
      {
        isLastChild: cellIndex === cellBoundingBoxes.length - 1,
      }
    );
  }
}

interface RenderTableCellOptions
  extends
    BaseRenderNodeOptions,
    Pick<
      RenderTableCellBordersOptions,
      'columnWidthsPx' | 'nextRMTableCell' | 'nextRMTableRow' | 'prevRMTableCell' | 'prevRMTableRow'
    > {
  cMTablePath: Path | null;
  gridSelectedCellPathKeys: ReadonlySet<string> | null;
  insertRowEdit?: InsertTableRowEdit;
  removeRowEdit?: RemoveTableRowEdit;
}

function renderTableCell(
  renderContext: RenderContext,
  rMTableCellEntry: NodeEntry<RenderModelTableCell>,
  options: RenderTableCellOptions
) {
  const isReadOnly = renderContext.withContext('isReadOnly');
  const [rMTableCell, rMTableCellPath] = rMTableCellEntry;
  const {
    endColumnIndex,
    heightPx,
    renderedBorders,
    startColumnIndex,
    verticalMergeHeightPx,
    verticalMergeIsStart,
    widthPx,
  } = rMTableCell;
  // Tolerate a null/missing suggestedEdits (collaboration can round-trip [] to null).
  const suggestedEdits = rMTableCell.suggestedEdits ?? [];
  const { fillColor } = Queries.renderedTableCellShade(rMTableCell) ?? {};
  const {
    cMTablePath,
    columnWidthsPx,
    gridSelectedCellPathKeys,
    insertRowEdit,
    nextRMTableCell,
    nextRMTableRow,
    prevRMTableCell,
    prevRMTableRow,
    removeRowEdit,
    selectionHighlightAbove,
  } = options;
  const insertEdit = suggestedEdits.find(isInsertTableCellEdit) ?? insertRowEdit;
  const removeEdit = suggestedEdits.find(isRemoveTableCellEdit) ?? removeRowEdit;
  const isGridSelectionActiveForTable = gridSelectedCellPathKeys !== null;

  /**
   * With a grid selection active for this table, a cell is selected iff it belongs to the selected
   * rectangle. Otherwise a cell is selected if the first cell of its vertical-merge group is
   * entirely contained within the (linear) selection.
   */
  const isSelected = renderContext.withEditor(editor => {
    if (!editor.selection || Range.isCollapsed(editor.selection)) return false;

    const cMTableCellEntry = Queries.nodeFromRMLocation(editor, rMTableCellPath);
    if (!isTableCellEntry(cMTableCellEntry)) throw new Error('Expected table cell entry');

    if (gridSelectedCellPathKeys)
      return gridSelectedCellPathKeys.has(cMTableCellEntry[1].join(','));

    const [, vMStartCellCMPath] = Queries.getVerticallyMergedTableCellStart(
      editor,
      cMTableCellEntry
    );

    const vMStartCellCMRange = Editor.range(editor, vMStartCellCMPath);
    return Range.surrounds(editor.selection, vMStartCellCMRange);
  });

  const backgroundColor = insertEdit
    ? insertTableCellColor.toString()
    : removeEdit
      ? removeTableCellColor.toString()
      : fillColor;

  if (backgroundColor)
    renderContext.drawRect(0, 0, widthPx, heightPx, {
      fillColor: backgroundColor,
      zIndex: CANVAS_Z_INDICES.TABLE_CELL_BACKGROUND,
    });

  if (isSelected && !selectionHighlightAbove) {
    const isFocused = renderContext.withContext('isFocused');
    renderContext.drawRect(0, 0, widthPx, heightPx, {
      fillColor: isFocused ? activeSelectionColor.toString() : inactiveSelectionColor.toString(),
      zIndex: CANVAS_Z_INDICES.TABLE_CELL_SELECTION_HIGHLIGHT,
    });
  }

  if (verticalMergeIsStart)
    renderContext.withClipRect(0, 0, widthPx, verticalMergeHeightPx, () => {
      renderContext.renderChildren(rMTableCellEntry, renderContentBlockFns, {
        /**
         * Inherit remove edits but not insert edits, so that any text that will be removed by a
         * table-level edit will be struck through, but without falsely attributing text insertions
         * to the user who suggested inserting the cell.
         */
        inheritDeleteEdit: removeEdit,
        /**
         * While a grid selection is active for this table, every cell suppresses per-glyph text
         * selection painting: selected cells show a whole-cell highlight, and cells covered by the
         * linear range but outside the rectangle paint nothing (and no caret).
         */
        selectionHighlightAbove:
          selectionHighlightAbove || isSelected || isGridSelectionActiveForTable,
      } satisfies RenderContentBlockOptions);
    });

  renderTableCellBorders(renderContext, {
    columnWidthsPx,
    nextRMTableCell,
    nextRMTableRow,
    prevRMTableCell,
    prevRMTableRow,
    rMTableCell,
  });

  if (!isReadOnly && verticalMergeIsStart && cMTablePath) {
    if (!prevRMTableCell) {
      renderContext.renderReactComponent(
        0,
        0,
        'TableBorderResizer',
        {
          clippable: true,
          key: `${rMTableCellPath}-left-border-resizer`,
        },
        {
          borderWidth: Queries.renderedTableBorderWidth(renderedBorders.left, 'px'),
          cMTablePath,
          columnEdgeIndex: startColumnIndex,
          height: verticalMergeHeightPx,
        }
      );
    }

    renderContext.renderReactComponent(
      widthPx,
      0,
      'TableBorderResizer',
      {
        clippable: true,
        key: `${rMTableCellPath}-right-border-resizer`,
      },
      {
        borderWidth: Queries.renderedTableBorderWidth(renderedBorders.right, 'px'),
        cMTablePath,
        columnEdgeIndex: endColumnIndex + 1,
        height: verticalMergeHeightPx,
      }
    );
  }
}

interface RenderTableCellBordersOptions {
  columnWidthsPx: number[];
  nextRMTableCell: RenderModelTableCell | null;
  nextRMTableRow: RenderModelTableRow | null;
  prevRMTableCell: RenderModelTableCell | null;
  prevRMTableRow: RenderModelTableRow | null;
  rMTableCell: RenderModelTableCell;
}

function renderTableCellBorders(
  renderContext: RenderContext,
  {
    columnWidthsPx,
    nextRMTableCell,
    nextRMTableRow,
    prevRMTableCell,
    prevRMTableRow,
    rMTableCell,
  }: RenderTableCellBordersOptions
) {
  renderVerticalTableBorder(renderContext, {
    adjacentTableCell: prevRMTableCell ?? null,
    rMTableCell,
    side: 'left',
  });

  renderVerticalTableBorder(renderContext, {
    adjacentTableCell: nextRMTableCell ?? null,
    rMTableCell,
    side: 'right',
  });

  renderHorizontalTableBorders(renderContext, {
    adjacentTableRow: prevRMTableRow ?? null,
    columnWidthsPx,
    rMTableCell,
    side: 'top',
  });

  renderHorizontalTableBorders(renderContext, {
    adjacentTableRow: nextRMTableRow ?? null,
    columnWidthsPx,
    rMTableCell,
    side: 'bottom',
  });
}

interface RenderVerticalTableBorderOptions {
  /** The table cell adjoining the cell this border belongs to on the side of this border. */
  adjacentTableCell: RenderModelTableCell | null;
  rMTableCell: RenderModelTableCell;
  side: 'left' | 'right';
}

function renderVerticalTableBorder(
  renderContext: RenderContext,
  { adjacentTableCell, rMTableCell, side }: RenderVerticalTableBorderOptions
) {
  const border = rMTableCell.renderedBorders[side];
  if (!border.draw) return;

  const {
    heightPx,
    renderedBorders: { bottom, top },
    verticalMergeIsEnd,
    verticalMergeIsStart,
    widthPx,
  } = rMTableCell;

  const upperInsidePerpendicularBorder = verticalMergeIsStart
    ? top.at(side === 'left' ? 0 : -1)
    : undefined;

  const upperOutsidePerpendicularBorder = adjacentTableCell?.verticalMergeIsStart
    ? adjacentTableCell.renderedBorders.top.at(side === 'left' ? -1 : 0)
    : undefined;

  const lowerInsidePerpendicularBorder = verticalMergeIsEnd
    ? bottom.at(side === 'left' ? 0 : -1)
    : undefined;

  const lowerOutsidePerpendicularBorder = adjacentTableCell?.verticalMergeIsEnd
    ? adjacentTableCell.renderedBorders.bottom.at(side === 'left' ? -1 : 0)
    : undefined;

  const upperInsidePerpendicularBorderWidthPx = ptToPx(upperInsidePerpendicularBorder?.size ?? 0);
  const upperOutsidePerpendicularBorderWidthPx = ptToPx(upperOutsidePerpendicularBorder?.size ?? 0);
  const lowerInsidePerpendicularBorderWidthPx = ptToPx(lowerInsidePerpendicularBorder?.size ?? 0);
  const lowerOutsidePerpendicularBorderWidthPx = ptToPx(lowerOutsidePerpendicularBorder?.size ?? 0);

  renderTableBorderLine(renderContext, {
    border,
    crossAxisPosition: side === 'left' ? 0 : widthPx,
    mainAxisEnd: heightPx,
    mainAxisStart: 0,
    orientation: 'vertical',
    perpendicularBorderWidthEndPx: Math.max(
      lowerInsidePerpendicularBorderWidthPx,
      lowerOutsidePerpendicularBorderWidthPx
    ),
    perpendicularBorderWidthStartPx: Math.max(
      upperInsidePerpendicularBorderWidthPx,
      upperOutsidePerpendicularBorderWidthPx
    ),
  });
}

interface RenderHorizontalTableBordersOptions {
  /** The table row adjoining the row this border belongs to on the side of this border. */
  adjacentTableRow: RenderModelTableRow | null;
  columnWidthsPx: number[];
  rMTableCell: RenderModelTableCell;
  side: 'top' | 'bottom';
}

function renderHorizontalTableBorders(
  renderContext: RenderContext,
  { adjacentTableRow, columnWidthsPx, rMTableCell, side }: RenderHorizontalTableBordersOptions
) {
  const borders = rMTableCell.renderedBorders[side];
  const {
    endColumnIndex,
    heightPx,
    renderedBorders: { left, right },
    startColumnIndex,
    verticalMergeIsEnd,
    verticalMergeIsStart,
  } = rMTableCell;

  const drawBorders = side === 'top' ? verticalMergeIsStart : verticalMergeIsEnd;
  if (!drawBorders) return [];

  const leftOuterPerpendicularBorder = adjacentTableRow?.children.find(
    otherCell => otherCell.startColumnIndex === startColumnIndex
  )?.renderedBorders.left;

  const rightOuterPerpendicularBorder = adjacentTableRow?.children.find(
    otherCell => otherCell.endColumnIndex === endColumnIndex
  )?.renderedBorders.right;

  const leftInnerPerpendicularBorderWidthPx = Queries.renderedTableBorderWidth(left, 'px');
  const leftOuterPerpendicularBorderWidthPx = leftOuterPerpendicularBorder
    ? Queries.renderedTableBorderWidth(leftOuterPerpendicularBorder, 'px')
    : 0;
  const rightInnerPerpendicularBorderWidthPx = Queries.renderedTableBorderWidth(right, 'px');
  const rightOuterPerpendicularBorderWidthPx = rightOuterPerpendicularBorder
    ? Queries.renderedTableBorderWidth(rightOuterPerpendicularBorder, 'px')
    : 0;

  let borderStartX = 0;

  for (const [borderIndex, renderedBorder] of arrayEntries(borders)) {
    const isFirstBorder = borderIndex === 0;
    const isLastBorder = borderIndex === borders.length - 1;

    const columnIndex = startColumnIndex + borderIndex;
    const columnWidth = columnWidthsPx.at(columnIndex);
    if (columnWidth === undefined) continue;

    if (renderedBorder.draw)
      renderTableBorderLine(renderContext, {
        border: renderedBorder,
        crossAxisPosition: side === 'top' ? 0 : heightPx,
        mainAxisEnd: borderStartX + columnWidth,
        mainAxisStart: borderStartX,
        orientation: 'horizontal',
        perpendicularBorderWidthEndPx: isLastBorder
          ? Math.max(rightInnerPerpendicularBorderWidthPx, rightOuterPerpendicularBorderWidthPx)
          : 0,
        perpendicularBorderWidthStartPx: isFirstBorder
          ? Math.max(leftInnerPerpendicularBorderWidthPx, leftOuterPerpendicularBorderWidthPx)
          : 0,
      });

    borderStartX += columnWidth;
  }
}

interface RenderTableBorderLineOptions {
  border: TableBorder;
  /** The position of the border in the axis perpendicular to that of the border. */
  crossAxisPosition: number;
  /** The end of the border line in the axis of the border. */
  mainAxisEnd: number;
  /** The start of the border line in the axis of the border. */
  mainAxisStart: number;
  orientation: 'horizontal' | 'vertical';
  /** The size of the thickest border that perpendicularly intersects the end of this border. */
  perpendicularBorderWidthEndPx: number;
  /** The size of the thickest border that perpendicularly intersects the start of this border. */
  perpendicularBorderWidthStartPx: number;
}

/**
 * Get the draw instruction for a table border line segment.
 *
 * The start and end of the border are adjusted based on the thickness of the perpendicular borders,
 * to ensure that intersections between perpendicular borders are drawn correctly. If this border is
 * thinner than the perpendicular borders, it terminates early to avoid overlapping with the
 * perpendicular border. Otherwise, the start or end of this border is extended by half the
 * thickness of the perpendicular border, effectively giving this border a "line cap" that entirely
 * covers the rectangular area of intersection.
 */
function renderTableBorderLine(
  renderContext: RenderContext,
  {
    border,
    crossAxisPosition: baseCrossAxisPosition,
    mainAxisEnd,
    mainAxisStart,
    orientation,
    perpendicularBorderWidthEndPx,
    perpendicularBorderWidthStartPx,
  }: RenderTableBorderLineOptions
) {
  const { color, style } = border;

  const borderWidth = Queries.renderedTableBorderWidth(border, 'px');
  if (!borderWidth) return [];

  const overlapStart = borderWidth >= perpendicularBorderWidthStartPx;
  const startOffset = (perpendicularBorderWidthStartPx / 2) * (overlapStart ? 1 : -1);

  const overlapEnd = borderWidth >= perpendicularBorderWidthEndPx;
  const endOffset = (perpendicularBorderWidthEndPx / 2) * (overlapEnd ? 1 : -1);

  const mainAxisStartWithOffset = mainAxisStart - startOffset;
  const mainAxisEndWithOffset = mainAxisEnd + endOffset;
  const crossAxisPosition = baseCrossAxisPosition;

  const { endX, endY, startX, startY } = {
    horizontal: {
      endX: mainAxisEndWithOffset,
      endY: crossAxisPosition,
      startX: mainAxisStartWithOffset,
      startY: crossAxisPosition,
    },
    vertical: {
      endX: crossAxisPosition,
      endY: mainAxisEndWithOffset,
      startX: crossAxisPosition,
      startY: mainAxisStartWithOffset,
    },
  }[orientation];

  let dashPattern: number[] | undefined;

  // Only solid, dashed and dotted are supported at present
  switch (style) {
    case 'dashed':
      dashPattern = [borderWidth * 4, borderWidth * 4];
      break;

    case 'dotted':
      dashPattern = [borderWidth, borderWidth];
      break;
  }

  if (orientation === 'horizontal') {
    renderContext.drawHorizontalLine({
      color,
      dashPattern,
      endX,
      startX,
      thickness: borderWidth,
      y: startY,
    });
  } else {
    renderContext.drawVerticalLine({
      color,
      dashPattern,
      endY,
      startY,
      thickness: borderWidth,
      x: startX,
    });
  }
}
